import { createDomain, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { leadWelcomeDrawer } from '~/entities/Registration/ui/LeadWelcomeDrawer'
import { viewerModel } from '~/entities/viewer'
import { Auth, AuthCodeChannel, AxiosErrorType, User } from '~/shared/api'
import { replaceFx } from '~/shared/lib/history'
import { logger } from '~/shared/lib/logger'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { allSnackbarsClosed } from '~/shared/lib/notifications/model'
import { sentryModel } from '~/shared/lib/sentry'
import { LoginStepsEnum } from './constants'
import { stepLoginPhoneModel } from './StepLoginPhone'

export const Gate = createGate()
export const domain = createDomain('features.auth.login')

export const goToStepLoginPhone = domain.createEvent()
export const goToStepLoginCode = domain.createEvent()

export const $loginStep = createStore<LoginStepsEnum>(
  LoginStepsEnum.LOGIN_PHONE,
)
  .on(goToStepLoginPhone, () => LoginStepsEnum.LOGIN_PHONE)
  .on(goToStepLoginCode, () => LoginStepsEnum.LOGIN_CODE)
  .on([Gate.open, Gate.close], () => LoginStepsEnum.LOGIN_PHONE)

type RequestLoginCodeParams = {
  phone: stepLoginPhoneModel.LoginPhoneFormValues['phone']
  channel?: 'sms'
}
type RequestLoginCodeResult = {
  phone: string
  channel: AuthCodeChannel
  maskedEmail: string | null
}

export const requestLoginCodeFx = domain.createEffect<
  RequestLoginCodeParams,
  RequestLoginCodeResult,
  AxiosErrorType
>({
  async handler({ phone, channel }) {
    const { channel: resolvedChannel, maskedEmail } = await Auth.code(
      phone,
      channel,
    )
    return { phone, channel: resolvedChannel, maskedEmail }
  },
})

export const $loginCodeChannel = createStore<{
  channel: AuthCodeChannel
  maskedEmail: string | null
} | null>(null)
  .on(requestLoginCodeFx.doneData, (_, { channel, maskedEmail }) => ({
    channel,
    maskedEmail,
  }))
  .on([Gate.open, Gate.close], () => null)

sample({
  clock: requestLoginCodeFx.doneData,
  fn({ phone, channel, maskedEmail }) {
    return {
      message:
        channel === 'email'
          ? `Код подтверждения входа отправлен на почту ${maskedEmail}`
          : `Код подтверждения входа отправлен на номер +${phone}`,
      variant: 'success' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: requestLoginCodeFx.failData,
  fn(e) {
    return {
      message: mapMessageErrors(e),
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

export const loginAfterFullRegistration = domain.createEvent<{
  token: string
  phone: string
}>()

type LoginParams = {
  phone: stepLoginPhoneModel.LoginPhoneFormValues['phone']
  code: string
}
type LoginResultParams = { token: string; phone: string }

const login = async ({
  phone,
  code,
}: LoginParams): Promise<LoginResultParams> => {
  const result = await User.login({ phone, authCode: code })
  return { ...result, phone }
}

export const loginFx = domain.createEffect<
  LoginParams,
  LoginResultParams,
  AxiosErrorType
>({
  handler: login,
})

export const leadLoginFx = domain.createEffect<
  LoginParams,
  LoginResultParams,
  AxiosErrorType
>({
  handler: login,
})

sample({
  clock: [loginFx.doneData, leadLoginFx.doneData, loginAfterFullRegistration],
  fn({ token }) {
    return token
  },
  target: viewerModel.tokenSaved,
})

sample({
  clock: [loginFx.doneData, leadLoginFx.doneData, loginAfterFullRegistration],
  target: allSnackbarsClosed,
})

sample({
  clock: [loginFx.doneData, leadLoginFx.doneData, loginAfterFullRegistration],
  fn({ phone }) {
    return phone
  },
  target: [viewerModel.phoneSaved, sentryModel.setUserPhone],
})

sample({
  clock: [loginFx.failData, leadLoginFx.failData],
  filter: (e) => e?.response?.status === 401,
  fn(e) {
    logger.error(e)
    return {
      message: 'Не верный код авторизации',
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: [loginFx.failData, leadLoginFx.failData],
  filter: (e) => e?.response?.status === 429,
  fn(e) {
    logger.error(e)
    return {
      message:
        'Превышен лимит попыток. Пожалуйста, попробуйте снова через 1 минут',
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: [loginFx.failData, leadLoginFx.failData],
  filter: (e) => e?.response?.status !== 429 && e?.response?.status !== 401,
  fn(e) {
    logger.error(e)
    return {
      message: mapMessageErrors(e),
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: [loginFx.doneData, leadLoginFx.doneData],
  fn() {
    return '/'
  },
  target: replaceFx,
})

sample({
  clock: leadLoginFx.doneData,
  target: leadWelcomeDrawer.showLeadWelcomeDrawer,
})
